import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Container,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Delete, Edit, EventBusy, ExpandLess, ExpandMore } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import AddButton from "../components/AddButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ResultCount from "../components/ResultCount";
import ConfirmModal from "../components/modals/ConfirmModal";
import ScheduleBlockModal, { ScheduleBlockCreateData, ScheduleBlockUpdateData } from "../components/modals/ScheduleBlockModal";
import { Toast } from "../components/Toast";
import { useToast } from "../hooks/useToast";
import { useNavigation } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { useSchedulePermissions } from "../hooks/useSchedulePermissions";
import { colors, pageHeader } from "../theme/designSystem";
import { schedulesService, ScheduleOptions } from "../services/schedulesService";
import {
  scheduleBlocksService,
  mapScheduleBlockError,
  ScheduleBlockItem,
  ScheduleBlockOccurrence,
  ScheduleBlockSeriesItem,
} from "../services/scheduleBlocksService";
import { WEEKDAYS_PT } from "../components/schedule/seriesShared";

// yyyy-MM-dd(THH:mm...) → dd/MM/yyyy sem passar por Date (evita deslocamento de fuso)
const formatDay = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
const hourOf = (iso: string) => iso.slice(11, 16);
const weekdayOf = (iso: string) => {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return WEEKDAYS_PT[new Date(y, m - 1, d).getDay()];
};

const whenLabel = (s: ScheduleBlockSeriesItem) => {
  const days = s.weekdays.map(d => WEEKDAYS_PT[d]).join(", ");
  const hours = s.startTime && s.endTime ? `${s.startTime}–${s.endTime}` : "horários variados";
  return `${days} · ${hours}`;
};

// Botão de ação da linha no padrão do sistema (32px no desktop, 44px no celular pela classe)
const rowActionSx = (background: string, hover: string) => ({
  backgroundColor: background,
  color: colors.white,
  "&:hover": { backgroundColor: hover },
});

type EditTarget = { block: ScheduleBlockItem; seriesWide: boolean } | null;
type DeleteTarget = { id: string; includeFollowing: boolean; message: string } | null;

/**
 * Bloqueios de agenda (RN-118n): uma linha por série, com as ocorrências dentro. O que já passou
 * é histórico (só leitura); editar e excluir valem do próximo bloqueio em diante.
 */
const NO_BLOCK_EDIT = "Seu perfil não cria nem edita bloqueios";
const NO_BLOCK_DELETE = "Seu perfil não exclui bloqueios";

const ScheduleBlocks: React.FC = () => {
  const { user } = useAuth();
  const schedulePerms = useSchedulePermissions();
  const { goToDashboard } = useNavigation();
  const { toast, showToast, hideToast } = useToast();

  const [options, setOptions] = useState<ScheduleOptions | null>(null);
  const [series, setSeries] = useState<ScheduleBlockSeriesItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [professionalFilter, setProfessionalFilter] = useState("all");
  const [titleFilter, setTitleFilter] = useState("");
  const [includePast, setIncludePast] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const [creating, setCreating] = useState(false);
  const [editTarget, setEditTarget] = useState<EditTarget>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget>(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setSeries(await scheduleBlocksService.getSeries("", includePast));
    } catch {
      showToast("Erro ao carregar os bloqueios.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Clinic4Us - Bloqueios de agenda";
    schedulesService.getOptions()
      .then(setOptions)
      .catch(() => showToast("Não foi possível carregar os profissionais.", "error"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includePast]);

  // Filtro de profissional: os que têm bloqueio na lista (inclui quem ficou inativo depois)
  const professionalOptions = useMemo(() => {
    const byId = new Map<string, string>();
    series.forEach(s => byId.set(s.professionalRegistrationId, s.professionalName || "Profissional"));
    return Array.from(byId, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
  }, [series]);

  // Quem o perfil pode bloquear: ativos; escrita 'own' só a própria agenda (RN-115b)
  const blockProfessionals = (options?.professionals ?? [])
    .filter(p => p.active)
    .filter(p => options?.writeScope !== "own" || options.ownProfessionalIds.includes(p.id))
    .map(p => ({ id: p.id, name: p.name }));

  const filtered = useMemo(() => {
    const term = titleFilter.trim().toLocaleLowerCase("pt-BR");
    return series
      .filter(s => professionalFilter === "all" || s.professionalRegistrationId === professionalFilter)
      .filter(s => !term || s.title.toLocaleLowerCase("pt-BR").includes(term));
  }, [series, professionalFilter, titleFilter]);

  const hasFilterChanges = professionalFilter !== "all" || titleFilter !== "" || includePast;
  const clearFilters = () => {
    setProfessionalFilter("all");
    setTitleFilter("");
    setIncludePast(false);
  };

  const toggleExpanded = (seriesId: string) =>
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(seriesId)) next.delete(seriesId); else next.add(seriesId);
      return next;
    });

  // Ocorrência → formato do modal; "seguintes" = por vir depois dela na mesma série
  const toBlockItem = (s: ScheduleBlockSeriesItem, o: ScheduleBlockOccurrence): ScheduleBlockItem => ({
    id: o.id,
    professionalRegistrationId: s.professionalRegistrationId,
    dateTimeInitial: o.dateTimeInitial,
    dateTimeFinal: o.dateTimeFinal,
    title: s.title,
    notes: s.notes,
    seriesId: s.seriesId,
    followingCount: s.occurrences.filter(x => !x.isPast && x.dateTimeInitial > o.dateTimeInitial).length,
    canEdit: s.canEdit,
    isPast: o.isPast,
  });

  const openSeriesEdit = (s: ScheduleBlockSeriesItem) => {
    const next = s.occurrences.find(o => o.id === s.nextId);
    if (next) setEditTarget({ block: toBlockItem(s, next), seriesWide: true });
  };

  const askSeriesDelete = (s: ScheduleBlockSeriesItem) => {
    if (!s.nextId) return;
    setDeleteTarget({
      id: s.nextId,
      includeFollowing: true,
      message: `Excluir ${s.futureCount === 1 ? "o próximo bloqueio" : `os ${s.futureCount} próximos bloqueios`} "${s.title}" de ${s.professionalName}? Os que já passaram ficam como histórico.`,
    });
  };

  const askOccurrenceDelete = (s: ScheduleBlockSeriesItem, o: ScheduleBlockOccurrence) =>
    setDeleteTarget({
      id: o.id,
      includeFollowing: false,
      message: `Excluir o bloqueio "${s.title}" de ${weekdayOf(o.dateTimeInitial)} ${formatDay(o.dateTimeInitial)}, ${hourOf(o.dateTimeInitial)}–${hourOf(o.dateTimeFinal)}?`,
    });

  const handleCreate = async (data: ScheduleBlockCreateData) => {
    try {
      const result = await scheduleBlocksService.create(data);
      showToast(result.ids.length > 1 ? `${result.ids.length} horários bloqueados.` : "Horário bloqueado.", "success");
      await load();
    } catch (error: any) {
      throw new Error(mapScheduleBlockError(error));
    }
  };

  const handleUpdate = async (id: string, data: ScheduleBlockUpdateData) => {
    try {
      const updated = await scheduleBlocksService.update(id, data);
      showToast(updated > 1 ? `${updated} bloqueios atualizados.` : "Bloqueio atualizado.", "success");
      await load();
    } catch (error: any) {
      throw new Error(mapScheduleBlockError(error));
    }
  };

  const handleDelete = async (id: string, includeFollowing: boolean) => {
    try {
      const deleted = await scheduleBlocksService.remove(id, includeFollowing);
      showToast(deleted > 1 ? `${deleted} bloqueios excluídos.` : "Bloqueio excluído.", "success");
      await load();
    } catch (error: any) {
      throw new Error(mapScheduleBlockError(error));
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await handleDelete(deleteTarget.id, deleteTarget.includeFollowing);
      setDeleteTarget(null);
    } catch (e: any) {
      showToast(e?.message || "Erro ao excluir o bloqueio.", "error");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={goToDashboard}
      />

      <Box component="main" sx={{ padding: "1rem", minHeight: "calc(100vh - 120px)", background: colors.background, marginTop: "85px", flex: 1 }}>
        <Container maxWidth={false} disableGutters sx={pageHeader.container}>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">Bloqueios de agenda</Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Reuniões, almoços e horários fechados dos profissionais. É só marcação: não é atendimento, não gera
                cobrança e não impede agendar. O que já passou fica como histórico.
              </Typography>
            </Box>
            <Box className="c4u-page-header__actions">
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel">
            <div className="c4u-filters__row">
              <div className="c4u-filters__fields">
                <FormControl size="small" fullWidth>
                  <InputLabel shrink>Profissional</InputLabel>
                  <Select label="Profissional" value={professionalFilter} onChange={(e) => setProfessionalFilter(e.target.value)} notched displayEmpty>
                    <MenuItem value="all">Todos</MenuItem>
                    {professionalOptions.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  fullWidth
                  label="Tipo de bloqueio"
                  placeholder="Ex.: Almoço"
                  value={titleFilter}
                  onChange={(e) => setTitleFilter(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <FormControl size="small" fullWidth>
                  <InputLabel shrink>Situação</InputLabel>
                  <Select label="Situação" value={includePast ? "all" : "upcoming"} onChange={(e) => setIncludePast(e.target.value === "all")} notched>
                    <MenuItem value="upcoming">Por vir e encerrados há até 30 dias</MenuItem>
                    <MenuItem value="all">Incluir todos os encerrados</MenuItem>
                  </Select>
                </FormControl>
              </div>
              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilterChanges || loading} />
                {schedulePerms.canBlockEdit && <AddButton onClick={() => setCreating(true)} title="Novo bloqueio" />}
              </div>
            </div>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress sx={{ color: colors.primary }} />
              </Box>
            ) : filtered.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 6, color: colors.textSecondary }}>
                <EventBusy sx={{ fontSize: 48, color: "#ced4da", mb: 1 }} />
                <Typography variant="body1" sx={{ mb: 0.5, color: colors.textPrimary }}>
                  {series.length === 0 ? "Nenhum bloqueio por vir nem encerrado nos últimos 30 dias" : "Nenhum bloqueio no filtro selecionado"}
                </Typography>
                <Typography variant="body2">
                  {series.length === 0
                    ? "Use \"Novo bloqueio\" ou o ícone vermelho na Agenda para marcar reunião, almoço ou horário fechado."
                    : "Ajuste o profissional, o tipo ou a situação."}
                </Typography>
              </Box>
            ) : (
              <div>
                <ResultCount shown={filtered.length} total={series.length} />
                <Box className="c4u-table__header">
                  <Box className="admin-plans-header-cell" sx={{ flex: "1 1 160px", minWidth: 0 }}>Profissional</Box>
                  <Box className="admin-plans-header-cell" sx={{ flex: "1 1 150px", minWidth: 0 }}>Tipo</Box>
                  <Box className="admin-plans-header-cell" sx={{ flex: "1 1 170px", minWidth: 0 }}>Quando</Box>
                  <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: "0 0 190px" }}>Período</Box>
                  <Box className="admin-plans-header-cell" sx={{ flex: "0 0 100px" }}>Por vir</Box>
                  <Box className="admin-plans-header-cell" sx={{ flex: "0 0 124px" }}>Ações</Box>
                </Box>

                {filtered.map(s => {
                  const open = expanded.has(s.seriesId);
                  const editable = s.canEdit && !!s.nextId;
                  return (
                    <React.Fragment key={s.seriesId}>
                      <Box className="c4u-table__row" sx={{ opacity: s.nextId ? 1 : 0.7 }}>
                        <Typography className="admin-plans-cell" data-label="Profissional" sx={{ flex: "1 1 160px", minWidth: 0, fontSize: "0.875rem", color: colors.textPrimary, wordBreak: "break-word" }}>
                          {s.professionalName || "—"}
                        </Typography>
                        <Box className="admin-plans-cell" data-label="Tipo" sx={{ flex: "1 1 150px", minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: colors.textPrimary, wordBreak: "break-word" }}>{s.title}</Typography>
                          {s.notes && (
                            <Typography sx={{ fontSize: "0.78rem", color: colors.textSecondary, wordBreak: "break-word" }}>{s.notes}</Typography>
                          )}
                        </Box>
                        <Typography className="admin-plans-cell" data-label="Quando" sx={{ flex: "1 1 170px", minWidth: 0, fontSize: "0.85rem", color: colors.textPrimary, fontVariantNumeric: "tabular-nums" }}>
                          {whenLabel(s)}
                        </Typography>
                        <Typography className="admin-plans-cell c4u-col--md" data-label="Período" sx={{ flex: "0 0 190px", fontSize: "0.85rem", color: colors.textSecondary, fontVariantNumeric: "tabular-nums" }}>
                          {s.total === 1 ? formatDay(s.firstDate) : `${formatDay(s.firstDate)} a ${formatDay(s.lastDate)}`}
                        </Typography>
                        <Typography className="admin-plans-cell" data-label="Por vir" sx={{ flex: "0 0 100px", fontSize: "0.85rem", color: s.nextId ? colors.textPrimary : colors.textSecondary, fontVariantNumeric: "tabular-nums" }}>
                          {s.nextId ? `${s.futureCount} de ${s.total}` : "encerrado"}
                        </Typography>
                        <Box className="admin-plans-cell admin-plans-actions" data-label="Ações" sx={{ flex: "0 0 124px", display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                          {s.total > 1 && (
                            <IconButton
                              className="c4u-row-action"
                              size="small"
                              onClick={() => toggleExpanded(s.seriesId)}
                              title={open ? "Recolher as datas" : "Ver as datas"}
                              aria-expanded={open}
                              aria-label={open ? "Recolher as datas" : "Ver as datas"}
                              sx={rowActionSx(colors.textSecondary, colors.textPrimary)}
                            >
                              {open ? <ExpandLess sx={{ fontSize: "1rem" }} /> : <ExpandMore sx={{ fontSize: "1rem" }} />}
                            </IconButton>
                          )}
                          {editable && (
                            <>
                              {/* RN-115d: sem a permissão o botão fica desabilitado e não abre nada */}
                              <span title={schedulePerms.canBlockEdit ? undefined : NO_BLOCK_EDIT}>
                                <IconButton
                                  className="c4u-row-action"
                                  size="small"
                                  disabled={!schedulePerms.canBlockEdit}
                                  onClick={() => openSeriesEdit(s)}
                                  title={s.futureCount > 1 ? "Editar do próximo em diante" : "Editar"}
                                  aria-label="Editar bloqueio"
                                  sx={rowActionSx(colors.primary, colors.primaryHover)}
                                >
                                  <Edit sx={{ fontSize: "1rem" }} />
                                </IconButton>
                              </span>
                              <span title={schedulePerms.canBlockDelete ? undefined : NO_BLOCK_DELETE}>
                                <IconButton
                                  className="c4u-row-action"
                                  size="small"
                                  disabled={!schedulePerms.canBlockDelete}
                                  onClick={() => askSeriesDelete(s)}
                                  title={s.futureCount > 1 ? "Excluir os próximos" : "Excluir"}
                                  aria-label="Excluir bloqueio"
                                  sx={rowActionSx("#dc3545", "#c82333")}
                                >
                                  <Delete sx={{ fontSize: "1rem" }} />
                                </IconButton>
                              </span>
                            </>
                          )}
                        </Box>
                      </Box>

                      {open && (
                        <Box sx={{
                          padding: { xs: "0.5rem 0.75rem 0.75rem", sm: "0.5rem 1rem 0.75rem 2rem" },
                          backgroundColor: colors.backgroundSubtle,
                          borderBottom: `1px solid ${colors.borderLight}`,
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", sm: "repeat(auto-fill, minmax(260px, 1fr))" },
                          gap: "0.25rem 1rem",
                        }}>
                          {s.occurrences.map(o => (
                            <Box key={o.id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.5rem", minHeight: 36 }}>
                              <Typography sx={{
                                fontSize: "0.85rem",
                                color: o.isPast ? colors.textSecondary : colors.textPrimary,
                                fontVariantNumeric: "tabular-nums",
                              }}>
                                {`${weekdayOf(o.dateTimeInitial)} ${formatDay(o.dateTimeInitial)}, ${hourOf(o.dateTimeInitial)}–${hourOf(o.dateTimeFinal)}`}
                                {o.isPast ? " · passou" : ""}
                              </Typography>
                              {s.canEdit && !o.isPast && (
                                <Box sx={{ display: "flex", gap: "0.125rem" }}>
                                  <span title={schedulePerms.canBlockEdit ? undefined : NO_BLOCK_EDIT}>
                                    <IconButton size="small" disabled={!schedulePerms.canBlockEdit}
                                      onClick={() => setEditTarget({ block: toBlockItem(s, o), seriesWide: false })}
                                      title="Editar esta data" aria-label="Editar esta data" sx={{ color: colors.primary }}>
                                      <Edit sx={{ fontSize: "1rem" }} />
                                    </IconButton>
                                  </span>
                                  <span title={schedulePerms.canBlockDelete ? undefined : NO_BLOCK_DELETE}>
                                    <IconButton size="small" disabled={!schedulePerms.canBlockDelete}
                                      onClick={() => askOccurrenceDelete(s, o)}
                                      title="Excluir esta data" aria-label="Excluir esta data" sx={{ color: colors.textSecondary, "&:hover": { color: colors.error } }}>
                                      <Delete sx={{ fontSize: "1rem" }} />
                                    </IconButton>
                                  </span>
                                </Box>
                              )}
                            </Box>
                          ))}
                        </Box>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </div>
        </Container>
      </Box>

      <ScheduleBlockModal
        isOpen={creating || editTarget !== null}
        onClose={() => { setCreating(false); setEditTarget(null); }}
        professionals={editTarget
          ? [{ id: editTarget.block.professionalRegistrationId, name: series.find(x => x.seriesId === editTarget.block.seriesId)?.professionalName ?? "" }]
          : blockProfessionals}
        defaults={{
          professionalId: options?.writeScope === "own"
            ? options.ownProfessionalIds[0]
            : professionalFilter !== "all" && blockProfessionals.some(p => p.id === professionalFilter) ? professionalFilter : undefined,
        }}
        block={editTarget?.block ?? null}
        defaultApplyToFollowing={editTarget?.seriesWide ?? false}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        allowEdit={schedulePerms.canBlockEdit}
        allowDelete={schedulePerms.canBlockDelete}
        businessHours={options?.businessHours}
        loadBlocks={(professionalId, from, to) => scheduleBlocksService.getBlocks(from, to, professionalId)}
        loadAppointments={(professionalId, from, to) => schedulesService.findConflicts(professionalId, null, from, to)}
        emptyReason={options?.writeScope === "own"
          ? "Seu perfil só bloqueia a própria agenda, e seu usuário não tem cadastro de profissional ativo nesta clínica."
          : "Não há profissional ativo para bloquear."}
      />

      <ConfirmModal
        isOpen={deleteTarget !== null}
        title="Excluir bloqueio"
        message={deleteTarget?.message ?? ""}
        type="warning"
        confirmButtonText="Excluir"
        cancelButtonText="Cancelar"
        isProcessing={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <FooterInternal className="login-footer-component" />
      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
    </Box>
  );
};

export default ScheduleBlocks;
